import classNames from 'classnames';
import React, { FC, PropsWithChildren, useRef } from 'react';

import { IcCloseIcon, IcPlayListIcon } from '@gem/icons';
import { noop } from '@gem/utils';

import { ClickOutside } from '../../../ClickOutside/ClickOutside';
import { FAB } from '../../../FAB/FAB';
import { MediaPlayerProgress } from '../../components/MediaPlayerProgress/MediaPlayerProgress';
import { IAudioPlayerConfig, IAudioPlayerTrack } from '../AudioPlayer.types';
import { AudioPlayerControls } from './AudioPlayerControls/AudioPlayerControls';
import { AudioPlayerPlayList } from './AudioPlayerPlayList/AudioPlayerPlayList';
import { AudioPlayerTitle } from './AudioPlayerTitle/AudioPlayerTitle';
import styles from './AudioPlayerUI.styles.m.css';

export interface IAudioPlayerUIProps {
  className?: string;
  currentSpeed: number;
  isPlaying: boolean;
  isShowPlayList?: boolean;
  isPlayListOpened?: boolean;
  isShowOrder?: boolean;
  isShowRepeat?: boolean;
  hasPrevious?: boolean;
  hasNext?: boolean;
  config: IAudioPlayerConfig;
  track: IAudioPlayerTrack | null;
  playList: IAudioPlayerTrack[];
  progress: number;
  progressMax: number;
  speedTooltipText?: string;
  onPlay(): void;
  onPause(): void;
  onTogglePlay(): void;
  onTogglePlayList?(): void;
  onClosePlayList?(): void;
  onChangeTrack(track: IAudioPlayerTrack): void;
  onChangeProgress(progress: number): void;
  onTitleClick(): void;
  onChangeConfig(config: Partial<IAudioPlayerConfig>): void;
  onChangeSpeed(speed: number): void;
  onPrevious?(): void;
  onNext?(): void;
  onClose?(): void;
}

export const AudioPlayerUI: FC<PropsWithChildren<IAudioPlayerUIProps>> = (props) => {
  const {
    className,
    currentSpeed,
    children,
    isPlaying,
    isShowPlayList = false,
    isPlayListOpened = false,
    isShowOrder = false,
    isShowRepeat = false,
    hasPrevious = false,
    hasNext = false,
    config,
    track,
    playList,
    progress,
    progressMax,
    speedTooltipText,
    onPlay,
    onPause,
    onTogglePlay,
    onTogglePlayList = noop,
    onClosePlayList = noop,
    onChangeTrack,
    onChangeProgress,
    onTitleClick,
    onChangeConfig,
    onChangeSpeed,
    onPrevious = noop,
    onNext = noop,
    onClose,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <ClickOutside
      reference={containerRef.current}
      onClickOutside={isShowPlayList ? onClosePlayList : undefined}
    >
      <div
        className={classNames(styles.container, className)}
        ref={containerRef}
      >
        {isShowPlayList && (
          <AudioPlayerPlayList
            isPlaying={isPlaying}
            activeTrack={track}
            items={playList}
            progress={progress}
            isOpened={isPlayListOpened}
            onTogglePlay={onTogglePlay}
            onChangeTrack={onChangeTrack}
          />
        )}

        <div className={classNames(styles.content, { [styles.listOpened]: isPlayListOpened })}>
          <div className={styles.panel}>
            {isShowPlayList && (
              <FAB
                Icon={IcPlayListIcon}
                size="10"
                iconSize="10"
                theme="15"
                onClick={onTogglePlayList}
              />
            )}

            <AudioPlayerTitle
              className={styles.title}
              title={track?.title || ''}
              description={track?.description || ''}
              onClick={onTitleClick}
            />

            {onClose && (
              <FAB
                Icon={IcCloseIcon}
                size="10"
                iconSize="10"
                theme="15"
                onClick={onClose}
              />
            )}
          </div>

          <div className={styles.controls}>
            <MediaPlayerProgress
              className={styles.progress}
              isPlaying={isPlaying}
              progress={progress}
              progressMax={progressMax}
              isShowBuffer={false}
              isShowTooltip={true}
              onPlay={onPlay}
              onPause={onPause}
              onProgress={onChangeProgress}
            />

            <AudioPlayerControls
              isPlaying={isPlaying}
              config={config}
              currentSpeed={currentSpeed}
              isShowOrder={isShowOrder}
              isShowRepeat={isShowRepeat}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              speedTooltipText={speedTooltipText}
              onTogglePlay={onTogglePlay}
              onPrevious={onPrevious}
              onNext={onNext}
              onConfigUpdate={onChangeConfig}
              onChangeSpeed={onChangeSpeed}
            />
          </div>

          {children}
        </div>
      </div>
    </ClickOutside>
  );
};
